/**
 * The native TanStack title mutation.
 *
 * Writes go to a dedicated Rails action rather than `POST /_graphql`: the client sends the issue
 * *number* in the path, so the endpoint can only ever write the issue the URL already authorized.
 */
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {UpdateIssueTitleMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueTitleMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

import {ERRORS} from '../constants/errors'
import {escapeOptimisticTitleHTML} from '../utils/helpers'

type UpdateIssueTitleVariables = {nameWithOwner: string; issueNumber: number; title: string}

const titleOf = (operation: SetFieldsOperation): string => String(operation.fields.title ?? '')

/**
 * RELAY-CLEANUP(IssueViewerIssue): the header can still render `title`/`titleHTML` from a
 * Relay-backed document, so mirror the operation until `IssueViewerViewQuery` is migrated.
 */
function writeOptimisticTitle(store: RecordSourceProxy, id: string, title: string): void {
  const record = store.get(id)
  if (!record) return
  record.setValue(title, 'title')
  record.setValue(escapeOptimisticTitleHTML(title), 'titleHTML')
}

function writeConfirmedTitle(store: RecordSourceProxy, id: string, title: string, titleHTML: string): void {
  const record = store.get(id)
  if (!record) return
  record.setValue(title, 'title')
  // Already server-rendered markup from this mutation's `titleHTML` selection; escaping double-escapes.
  // eslint-disable-next-line @github-ui/github-monorepo/no-unsafe-titlehtml
  record.setValue(titleHTML, 'titleHTML')
}

async function updateIssueTitleRequest({
  nameWithOwner,
  issueNumber,
  title,
}: UpdateIssueTitleVariables): Promise<UpdateIssueTitleMutationMutation> {
  let response: Response
  try {
    response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/title`, {
      method: 'PUT',
      body: {title},
    })
  } catch {
    throw new Error(ERRORS.couldNotUpdateIssueTitle)
  }

  let result:
    | {data?: UpdateIssueTitleMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null}
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const errors = result?.errors
  if (!response.ok) {
    throw new Error(errors?.[0]?.message ?? ERRORS.couldNotUpdateIssueTitle)
  }

  if (!result?.data?.updateIssue?.issue) {
    throw new Error(errors?.[0]?.message ?? ERRORS.couldNotUpdateIssueTitle)
  }

  return result.data
}

export function useUpdateIssueTitle({
  issueId,
  nameWithOwner,
  issueNumber,
  onError,
  onCompleted,
}: {
  issueId: string
  nameWithOwner: string
  issueNumber: number
  onError?: (error: Error) => void
  onCompleted?: () => void
}) {
  const environment = useRelayEnvironment()

  const {mutate} = useEntityMutation<UpdateIssueTitleMutationMutation, Error, Disposable>({
    mutationFn: operation => updateIssueTitleRequest({nameWithOwner, issueNumber, title: titleOf(operation)}),
    onMutate: ({operation}) =>
      environment.applyUpdate({
        storeUpdater: store => writeOptimisticTitle(store, operation.id, titleOf(operation)),
      }),
    getConfirmedOperation: data => {
      const issue = data.updateIssue?.issue
      if (!issue) return undefined
      return {type: 'setFields', id: issue.id, fields: {title: issue.title, titleHTML: issue.titleHTML}}
    },
    onSuccess: (data, _variables, revertRelayStore) => {
      const issue = data.updateIssue?.issue
      if (issue) {
        commitLocalUpdate(environment, store => writeConfirmedTitle(store, issue.id, issue.title, issue.titleHTML))
      }
      // Fold in the confirmed value before dropping the optimistic layer; the reverse flashes the
      // pre-edit title between the two writes.
      revertRelayStore?.dispose()
      onCompleted?.()
    },
    onError: (error, _variables, revertRelayStore) => {
      revertRelayStore?.dispose()
      onError?.(error)
    },
  })

  return useCallback(
    (title: string) => {
      const operation: SetFieldsOperation = {
        type: 'setFields',
        id: issueId,
        fields: {title, titleHTML: escapeOptimisticTitleHTML(title)},
      }
      mutate({operation})
    },
    [issueId, mutate],
  )
}
